/**
 * Evaluation Criteria Configuration
 * Defines criteria for each evaluation round with maximum marks
 */

export const EVALUATION_CRITERIA = {
  round1: {
    totalMarks: 80,
    criteria: [
      { name: 'innovation', label: 'Innovation & Uniqueness', maxMarks: 10 },
      { name: 'aiApproach', label: 'AI/ML Approach', maxMarks: 10 },
      { name: 'feasibility', label: 'Feasibility', maxMarks: 10 },
      { name: 'technicalArchitecture', label: 'Technical Architecture', maxMarks: 10 },
      { name: 'domainRelevance', label: 'Domain Relevance', maxMarks: 10 },
      { name: 'impactScalability', label: 'Impact & Scalability', maxMarks: 10 },
      { name: 'teamPreparedness', label: 'Team Preparedness', maxMarks: 10 },
      { name: 'presentationQuality', label: 'Presentation Quality', maxMarks: 10 }
    ]
  },
  round2: {
    totalMarks: 100,
    criteria: [
      { name: 'workingPrototype', label: 'Working Prototype', maxMarks: 10 },
      { name: 'aiModelImplementation', label: 'AI/ML Model Implementation', maxMarks: 10 },
      { name: 'technicalImplementation', label: 'Technical Implementation', maxMarks: 10 },
      { name: 'progressSinceRound1', label: 'Progress Since Round 1', maxMarks: 10 },
      { name: 'dataHandling', label: 'Data Handling', maxMarks: 10 },
      { name: 'modelPerformance', label: 'Model Performance', maxMarks: 10 },
      { name: 'uiUxIntegration', label: 'UI/UX & Integration', maxMarks: 10 },
      { name: 'problemSolutionFit', label: 'Problem-Solution Fit', maxMarks: 10 },
      { name: 'innovationInExecution', label: 'Innovation in Execution', maxMarks: 10 },
      { name: 'teamCoordination', label: 'Team Coordination', maxMarks: 10 }
    ]
  },
  round3: {
    totalMarks: 120,
    criteria: [
      { name: 'aiInnovation', label: 'AI Innovation', maxMarks: 10 },
      { name: 'technicalExcellence', label: 'Technical Excellence', maxMarks: 10 },
      { name: 'completeness', label: 'Completeness', maxMarks: 10 },
      { name: 'aiModelQuality', label: 'AI Model Quality', maxMarks: 10 },
      { name: 'domainImpact', label: 'Domain Impact', maxMarks: 10 },
      { name: 'uiUxDesign', label: 'UI/UX Design', maxMarks: 10 },
      { name: 'liveDemo', label: 'Live Demo', maxMarks: 10 },
      { name: 'presentation', label: 'Presentation', maxMarks: 10 },
      { name: 'scalabilityViability', label: 'Scalability & Viability', maxMarks: 10 },
      { name: 'socialEnvironmentalImpact', label: 'Social/Env. Impact', maxMarks: 10 },
      { name: 'teamKnowledge', label: 'Team Knowledge', maxMarks: 10 },
      { name: 'wowFactor', label: 'Wow Factor', maxMarks: 10 }
    ]
  }
};

export const GRAND_TOTAL_MARKS = 300; // 80 + 100 + 120

/**
 * Get criteria for a specific round
 * @param {string} roundNumber - Round number (round1, round2, round3)
 * @returns {Object} Criteria configuration for the round
 */
export const getRoundCriteria = (roundNumber) => {
  return EVALUATION_CRITERIA[roundNumber] || null;
};

/**
 * Calculate total marks from criteria scores
 * @param {Object} criteriaScores - Object with criterion names as keys and scores as values
 * @param {string} roundNumber - Round number
 * @returns {number} Total marks for the round
 */
export const calculateCriteriaTotal = (criteriaScores, roundNumber) => {
  const roundConfig = getRoundCriteria(roundNumber);
  if (!roundConfig) return 0;
  
  return roundConfig.criteria.reduce((total, criterion) => {
    return total + (criteriaScores[criterion.name] || 0);
  }, 0);
};

/**
 * Validate criteria scores against round configuration
 * @param {Object} criteriaScores - Object with criterion names as keys and scores as values
 * @param {string} roundNumber - Round number
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCriteriaScores = (criteriaScores, roundNumber) => {
  const roundConfig = getRoundCriteria(roundNumber);
  if (!roundConfig) {
    return { isValid: false, errors: ['Invalid round number'] };
  }
  
  const errors = [];
  
  // Check if all criteria have scores
  roundConfig.criteria.forEach(criterion => {
    const score = criteriaScores[criterion.name];
    if (score === undefined || score === null || score === '') {
      errors.push(`${criterion.label} is required`);
    } else if (isNaN(score) || score < 0 || score > criterion.maxMarks) {
      errors.push(`${criterion.label} must be between 0 and ${criterion.maxMarks}`);
    }
  });
  
  // Check total does not exceed round maximum
  const total = calculateCriteriaTotal(criteriaScores, roundNumber);
  if (total > roundConfig.totalMarks) {
    const roundNames = {
      'round1': 'Round 1',
      'round2': 'Round 2', 
      'round3': 'Round 3'
    };
    const roundName = roundNames[roundNumber] || roundNumber;
    errors.push(`Total marks cannot exceed ${roundConfig.totalMarks} for ${roundName}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
